import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";

const streamFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
});

interface StreamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  classId: string;
  selectedStream: string | null;
}

export function StreamDialog({
  open,
  onOpenChange,
  classId,
  selectedStream,
}: StreamDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const form = useForm<z.infer<typeof streamFormSchema>>({
    resolver: zodResolver(streamFormSchema),
    defaultValues: {
      name: "",
    },
  });

  const { data: streams } = useQuery({
    queryKey: ['class_streams', classId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('class_streams')
        .select('*')
        .eq('class_id', classId);
      
      if (error) throw error;
      return data;
    },
  });

  const { data: classData } = useQuery({
    queryKey: ['school_class', classId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('school_classes')
        .select('*')
        .eq('id', classId)
        .single();
      
      if (error) throw error;
      return data;
    },
  });

  const createStream = useMutation({
    mutationFn: async (values: z.infer<typeof streamFormSchema>) => {
      if (!classData) throw new Error("Class not found");

      const { error } = await supabase
        .from('class_streams')
        .insert({
          name: values.name,
          class_id: classId,
          school_id: classData.school_id,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class_streams'] });
      onOpenChange(false);
      form.reset();
      toast({
        title: "Success",
        description: "Stream has been successfully created",
      });
    },
    onError: (error: any) => {
      console.error('Error creating stream:', error);
      toast({
        title: "Error Creating Stream",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  const editStream = useMutation({
    mutationFn: async (values: z.infer<typeof streamFormSchema>) => {
      const { error } = await supabase
        .from('class_streams')
        .update({
          name: values.name,
        })
        .eq('id', selectedStream);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['class_streams'] });
      onOpenChange(false);
      form.reset();
      toast({
        title: "Success",
        description: "Stream has been successfully updated",
      });
    },
    onError: (error: any) => {
      console.error('Error updating stream:', error);
      toast({
        title: "Error Updating Stream",
        description: error.message || "An unexpected error occurred",
        variant: "destructive",
      });
    },
  });

  useEffect(() => {
    if (selectedStream && streams) {
      const streamToEdit = streams.find(s => s.id === selectedStream);
      if (streamToEdit) {
        form.reset({
          name: streamToEdit.name,
        });
      }
    }
  }, [selectedStream, streams, form]);

  const onSubmit = (data: z.infer<typeof streamFormSchema>) => {
    if (selectedStream) {
      editStream.mutate(data);
    } else {
      createStream.mutate(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {selectedStream ? "Edit Stream" : "Create New Stream"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={createStream.isPending || editStream.isPending}
            >
              {(createStream.isPending || editStream.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {selectedStream ? "Update Stream" : "Create Stream"}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}